import React from 'react';
import { AlertTriangle, Clock, Info } from 'lucide-react';

interface RiskBadgeProps {
    level: 'Low' | 'Medium' | 'High';
    score: number;
    reasons?: string[];
}

export function RiskBadge({ level, score, reasons }: RiskBadgeProps) {
    if (!level) return null;

    const colors = {
        Low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        Medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
        High: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800'
    };

    const icon = {
        Low: <Clock className="w-3 h-3" />,
        Medium: <Info className="w-3 h-3" />,
        High: <AlertTriangle className="w-3 h-3" />
    };

    return (
        <div className="group relative inline-flex items-center">
            <div className={`
                flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border
                cursor-help transition-all
                ${colors[level] || colors.Low}
            `}>
                {icon[level]}
                <span>{level} Risk</span>
                {score > 0 && <span className="opacity-70 text-[10px]">({Math.round(score * 100)}%)</span>}
            </div>

            {/* Tooltip */}
            {reasons && reasons.length > 0 && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 
                    bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 
                    rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible 
                    transition-all z-50 text-xs text-left"
                >
                    <div className="font-semibold mb-1 text-slate-900 dark:text-slate-100">Risk Factors</div>
                    <ul className="space-y-1 list-disc pl-3 text-slate-600 dark:text-slate-400">
                        {reasons.map((r, i) => (
                            <li key={i}>{r}</li>
                        ))}
                    </ul>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-white dark:border-t-slate-900" />
                </div>
            )}
        </div>
    );
}
